import { capForPath, charmArtUrls, missingArt, storeCardArtFiles, type ArtFile } from './assets';
import { charmArtFiles } from './charmArt';
import { yieldToPaint } from './yieldPaint';

/** A few paintings at a time. A phone decoder stalls if all 48 cards start together. */
export const CHARM_LOAD_CONCURRENCY = 3;
const FILE_TIMEOUT_MS = 20_000;

/** One frame of a Phaser texture. Present when the key is already on screen. */
interface TextureFrame {
  image: CanvasImageSource | null;
  source: CanvasImageSource | null;
  isCanvas: boolean;
  width: number;
  height: number;
  glTexture?: unknown;
  flipY?: boolean;
  renderer?: {
    gl?: unknown;
    updateCanvasTexture?: (canvas: HTMLCanvasElement, texture: unknown, flipY?: boolean) => void;
  };
}

export interface CharmTextureTarget {
  exists(key: string): boolean;
  addImage(key: string, source: CanvasImageSource): unknown;
  get?(key: string): { getSourceImage(): unknown };
  remove?(key: string): void;
}

/** A texture counts as loaded only when it has pixels. A cleared canvas does not. */
export function paintCovers(width: number, height: number, opaqueSamples: number): boolean {
  if (width < 2 || height < 2) return false;
  return opaqueSamples > 0;
}

export interface CharmLoadHooks {
  decode: (url: string, timeoutMs: number) => Promise<CanvasImageSource | null>;
  fit?: (source: CanvasImageSource, cap: number) => CanvasImageSource;
  concurrency?: number;
  timeoutMs?: number;
  onSettle?: () => void;
  alive?: () => boolean;
}

function rank(file: ArtFile): number {
  if (file.path.includes('/badges/')) return 0;
  if (file.path.includes('/headers/')) return 1;
  if (file.path.includes('/frames/')) return 2;
  return 3;
}

async function pool(
  files: readonly ArtFile[],
  textures: CharmTextureTarget,
  hooks: CharmLoadHooks,
): Promise<void> {
  const concurrency = Math.max(1, hooks.concurrency ?? CHARM_LOAD_CONCURRENCY);
  const timeoutMs = hooks.timeoutMs ?? FILE_TIMEOUT_MS;
  const alive = hooks.alive ?? (() => true);
  let cursor = 0;

  const worker = async (): Promise<void> => {
    for (;;) {
      if (!alive()) return;
      const index = cursor;
      cursor += 1;
      const file = files[index];
      if (!file) return;
      if (textures.exists(file.key)) continue;
      const urls = charmArtUrls(file.path);
      for (const url of urls) {
        if (!alive()) return;
        const image = await hooks.decode(url, timeoutMs);
        if (!image || !alive()) continue;
        if (textures.exists(file.key)) break;
        const fitted = hooks.fit ? hooks.fit(image, capForPath(file.path)) : image;
        // Keep a fitted canvas in the document. If that copy has no pixels, use
        // the decoded file so the card is still a real image.
        const stable = (await retainImage(fitted)) ?? (fitted === image ? null : image);
        if (!stable || !alive()) continue;
        if (textures.exists(file.key)) break;
        try {
          textures.addImage(file.key, stable);
        } catch {
          // A rejected upload leaves the key empty so the png attempt can try again.
          continue;
        }
        // A fitted canvas is drawn on the main thread. Let the glen paint before the next one.
        await yieldToPaint();
        if (textures.exists(file.key)) break;
      }
    }
  };

  const workers = Math.min(concurrency, files.length);
  if (workers === 0) return;
  await Promise.all(Array.from({ length: workers }, () => worker()));
}

/**
 * Load charm paintings without the shared Phaser file queue.
 * Badges, headers, and frames settle first, then the cards, and `onSettle`
 * runs after each of those groups so the screen can drop the stand-ins.
 */
export async function loadCharmTextures(
  files: readonly ArtFile[],
  textures: CharmTextureTarget,
  hooks: CharmLoadHooks,
): Promise<void> {
  const missing = files.filter((file) => !textures.exists(file.key));
  const groups = [0, 1, 2, 3]
    .map((level) => missing.filter((file) => rank(file) === level))
    .filter((group) => group.length > 0);
  for (const group of groups) {
    if (hooks.alive && !hooks.alive()) return;
    await pool(group, textures, hooks);
    if (hooks.alive && !hooks.alive()) return;
    hooks.onSettle?.();
  }
}

function measuredSize(source: CanvasImageSource): { width: number; height: number } {
  const measured = source as CanvasImageSource & { naturalWidth?: number; naturalHeight?: number; width?: number; height?: number };
  return {
    width: measured.naturalWidth || measured.width || 0,
    height: measured.naturalHeight || measured.height || 0,
  };
}

/**
 * Count opaque pixels in the corners and the middle.
 * A frame is clear in the middle and solid in the corners. A discarded canvas is clear everywhere.
 */
function opaqueSamples(source: CanvasImageSource): number {
  const { width, height } = measuredSize(source);
  if (width < 2 || height < 2) return 0;
  if (typeof document === 'undefined') return 1;
  const spot = 8;
  const canvas = document.createElement('canvas');
  canvas.width = spot;
  canvas.height = spot;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return 1;
  const spots = [
    [0, 0],
    [Math.max(0, width - spot), 0],
    [0, Math.max(0, height - spot)],
    [Math.max(0, width - spot), Math.max(0, height - spot)],
    [Math.max(0, Math.floor(width / 2 - spot / 2)), Math.max(0, Math.floor(height / 2 - spot / 2))],
  ];
  let opaque = 0;
  for (const [sx, sy] of spots) {
    ctx.clearRect(0, 0, spot, spot);
    try {
      ctx.drawImage(source, sx, sy, spot, spot, 0, 0, spot, spot);
    } catch {
      return 0;
    }
    const data = ctx.getImageData(0, 0, spot, spot).data;
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] > 16) opaque += 1;
    }
    if (opaque > 0) return opaque;
  }
  return 0;
}

/**
 * The glen loader builds every menu after the charm pass and fills the phone
 * with wood canvases. Chrome then drops the backing store of a canvas that
 * only the texture manager is holding, but the key stays, so the card looks
 * empty and is never fetched again. A software canvas kept in the document
 * holds its pixels, and a decoded image does too.
 */
function isCanvas(source: CanvasImageSource): source is HTMLCanvasElement {
  return typeof HTMLCanvasElement !== 'undefined' && source instanceof HTMLCanvasElement;
}

/**
 * A zero-size clip, with each painting parked to its left.
 * Shifting only the box and letting it shrink-to-fit wraps the last canvas of
 * each row back over the phone, which is the torn jungle strip.
 */
export const ART_HOLD_STYLE = 'position:fixed;left:0;top:0;width:0;height:0;overflow:hidden;pointer-events:none';
export const ART_HOLD_CANVAS_STYLE = 'position:absolute;left:-4000px;top:0';

export function pinCanvas(canvas: HTMLCanvasElement): void {
  if (typeof document === 'undefined') return;
  const root = document.body ?? document.documentElement;
  if (!root) return;
  let hold = document.getElementById('charm-art-hold');
  if (!hold) {
    hold = document.createElement('div');
    hold.id = 'charm-art-hold';
    hold.setAttribute('aria-hidden', 'true');
    root.appendChild(hold);
  }
  hold.style.cssText = ART_HOLD_STYLE;
  canvas.style.cssText = ART_HOLD_CANVAS_STYLE;
  if (canvas.parentNode !== hold) hold.appendChild(canvas);
}

async function retainImage(source: CanvasImageSource): Promise<CanvasImageSource | null> {
  if (!isCanvas(source)) return source;
  const { width, height } = measuredSize(source);
  if (!paintCovers(width, height, opaqueSamples(source))) return null;
  pinCanvas(source);
  return source;
}

function readSource(textures: CharmTextureTarget, key: string): CanvasImageSource | null {
  if (!textures.get) return null;
  try {
    return textures.get(key).getSourceImage() as CanvasImageSource;
  } catch {
    return null;
  }
}

function sourceHasPaint(source: CanvasImageSource | null): boolean {
  if (!source) return false;
  const { width, height } = measuredSize(source);
  return paintCovers(width, height, opaqueSamples(source));
}

function isHeldCanvas(source: CanvasImageSource): boolean {
  return isCanvas(source) && source.parentElement?.id === 'charm-art-hold';
}

/**
 * Copy a decoded file onto a software canvas and keep that canvas in the document.
 * An image whose blob URL was already revoked can report a size and still draw nothing.
 */
function softwareCopy(source: CanvasImageSource, cap: number): HTMLCanvasElement | null {
  if (typeof document === 'undefined') return null;
  const { width, height } = measuredSize(source);
  if (width < 2 || height < 2) return null;
  const scale = Math.min(1, cap / width, cap / height);
  const tw = Math.max(1, Math.round(width * scale));
  const th = Math.max(1, Math.round(height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = tw;
  canvas.height = th;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  try {
    ctx.drawImage(source, 0, 0, tw, th);
  } catch {
    return null;
  }
  if (!paintCovers(tw, th, opaqueSamples(canvas))) return null;
  pinCanvas(canvas);
  return canvas;
}

/**
 * Put pixels into a key without dropping it.
 * Removing a texture a card is already showing nulls its WebGL texture.
 */
export function installPaintedSource(textures: CharmTextureTarget, key: string, source: CanvasImageSource): 'added' | 'replaced' | 'kept' {
  if (!textures.exists(key)) {
    textures.addImage(key, source);
    return 'added';
  }
  const frame = (textures.get?.(key) as { source?: TextureFrame[] } | undefined)?.source?.[0];
  if (!frame) return 'kept';
  frame.image = source;
  frame.source = source;
  frame.isCanvas = isCanvas(source);
  const size = measuredSize(source);
  if (size.width > 0) frame.width = size.width;
  if (size.height > 0) frame.height = size.height;
  if (isCanvas(source) && frame.glTexture && frame.renderer?.updateCanvasTexture) {
    try {
      frame.renderer.updateCanvasTexture(source, frame.glTexture, frame.flipY);
    } catch {
      // The canvas is already the source. The next upload can use it.
    }
  }
  return 'replaced';
}

function dropUnpainted(textures: CharmTextureTarget, files: readonly ArtFile[]): void {
  if (!textures.get || !textures.remove) return;
  for (const file of files) {
    if (!textures.exists(file.key)) continue;
    let source: CanvasImageSource | null = null;
    try {
      source = textures.get(file.key).getSourceImage() as CanvasImageSource;
    } catch {
      continue;
    }
    if (sourceHasPaint(source)) continue;
    textures.remove(file.key);
  }
}

function fitSource(source: CanvasImageSource, cap: number): CanvasImageSource {
  const measured = source as CanvasImageSource & { naturalWidth?: number; naturalHeight?: number; width?: number; height?: number };
  const width = measured.naturalWidth || measured.width || 0;
  const height = measured.naturalHeight || measured.height || 0;
  if (width < 2 || height < 2 || (width <= cap && height <= cap) || typeof document === 'undefined') return source;
  const scale = Math.min(cap / width, cap / height);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return source;
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas;
}

function decodeUrl(url: string, timeoutMs: number): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const image = new Image();
    let settled = false;
    const finish = (value: HTMLImageElement | null) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      resolve(value);
    };
    const timer = window.setTimeout(() => {
      image.onload = null;
      image.onerror = null;
      image.src = '';
      finish(null);
    }, timeoutMs);
    image.onload = () => {
      const ready = typeof image.decode === 'function' ? image.decode().catch(() => undefined) : Promise.resolve();
      void ready.then(() => finish(image.naturalWidth > 0 ? image : null));
    };
    image.onerror = () => finish(null);
    image.src = url;
  });
}

interface Flight {
  promise: Promise<void>;
  settle: Array<() => void>;
}

const flights = new Map<string, Flight>();

interface CharmScene {
  textures: CharmTextureTarget;
}

/**
 * Await these paintings. A key whose canvas or image has lost its pixels is
 * removed and fetched from the file again. Callers share one load per list.
 * The store is asleep under the glen loader while this runs, so the callback
 * is not limited to the active scene. Each caller decides whether it can paint.
 */
function ensurePainted(files: readonly ArtFile[], scene: CharmScene, onSettle?: () => void, attempt = 1): Promise<void> {
  const id = `${files[0]?.key ?? 'art'}:${files.length}`;
  dropUnpainted(scene.textures, files);
  const redraw = () => {
    onSettle?.();
  };
  if (missingArt(scene.textures, files).length === 0) return Promise.resolve();
  let flight = flights.get(id);
  if (!flight) {
    const settle: Array<() => void> = [];
    const promise = loadCharmTextures(files, scene.textures, {
      decode: decodeUrl,
      fit: fitSource,
      onSettle: () => {
        for (const fn of [...settle]) fn();
      },
    }).finally(() => {
      if (flights.get(id)?.promise === promise) flights.delete(id);
    });
    flight = { promise, settle };
    flights.set(id, flight);
  }
  flight.settle.push(redraw);
  return flight.promise.then(() => {
    dropUnpainted(scene.textures, files);
    if (attempt < 3 && missingArt(scene.textures, files).length > 0) {
      return ensurePainted(files, scene, onSettle, attempt + 1);
    }
    redraw();
  });
}

/** Charm cards and frames. */
export function ensureCharmArt(scene: CharmScene, onSettle?: () => void): Promise<void> {
  return ensurePainted(charmArtFiles(), scene, onSettle);
}

const STORE_CARD_ATTEMPTS = 3;

function storeCardsNeedWork(textures: CharmTextureTarget, files: readonly ArtFile[]): ArtFile[] {
  return files.filter((file) => {
    if (!textures.exists(file.key)) return true;
    const source = readSource(textures, file.key);
    return !source || !isHeldCanvas(source) || !sourceHasPaint(source);
  });
}

/** Decode files that are missing or blank, then write the canvas into the existing key. */
async function refetchStoreCards(files: readonly ArtFile[], textures: CharmTextureTarget): Promise<void> {
  const concurrency = Math.max(1, Math.min(CHARM_LOAD_CONCURRENCY, files.length));
  if (concurrency === 0) return;
  let cursor = 0;
  const worker = async (): Promise<void> => {
    for (;;) {
      const index = cursor;
      cursor += 1;
      const file = files[index];
      if (!file) return;
      for (const url of charmArtUrls(file.path)) {
        const image = await decodeUrl(url, FILE_TIMEOUT_MS);
        if (!image) continue;
        const fitted = fitSource(image, capForPath(file.path));
        const pinned = softwareCopy(fitted, capForPath(file.path)) ?? (fitted === image ? null : softwareCopy(image, capForPath(file.path)));
        if (!pinned) continue;
        installPaintedSource(textures, file.key, pinned);
        await yieldToPaint();
        if (sourceHasPaint(readSource(textures, file.key) ?? pinned)) break;
      }
    }
  };
  await Promise.all(Array.from({ length: concurrency }, () => worker()));
}

/**
 * Trick and shelf photos are images. Phaser revokes their blob URL after decode,
 * so a later memory purge leaves the key and an empty card frame.
 * Copy each painting onto a pinned canvas. A blank key is fetched again and
 * written in place, so a card that is already showing it keeps its texture.
 */
async function hardenStoreCards(textures: CharmTextureTarget): Promise<void> {
  const files = storeCardArtFiles();
  const refetch: ArtFile[] = [];
  for (const file of files) {
    if (!textures.exists(file.key)) {
      refetch.push(file);
      continue;
    }
    const source = readSource(textures, file.key);
    if (!source || !sourceHasPaint(source)) {
      refetch.push(file);
      await yieldToPaint();
      continue;
    }
    if (!isHeldCanvas(source)) {
      const copy = softwareCopy(source, capForPath(file.path));
      if (!copy) refetch.push(file);
      else installPaintedSource(textures, file.key, copy);
    }
    await yieldToPaint();
  }
  if (refetch.length > 0) await refetchStoreCards(refetch, textures);
}

function runStoreCards(scene: CharmScene, attempt: number): Promise<void> {
  const id = 'store-cards';
  let flight = flights.get(id);
  if (!flight) {
    const promise = hardenStoreCards(scene.textures).finally(() => {
      if (flights.get(id)?.promise === promise) flights.delete(id);
    });
    flight = { promise, settle: [] };
    flights.set(id, flight);
  }
  return flight.promise.then(() => {
    if (attempt < STORE_CARD_ATTEMPTS && storeCardsNeedWork(scene.textures, storeCardArtFiles()).length > 0) {
      return runStoreCards(scene, attempt + 1);
    }
    return undefined;
  });
}

/**
 * Trick icons, cosmetic photos, the spin mark, and reward cards.
 * These are the paintings on the Store pages. A discarded image still has a
 * texture key, so the card would keep an empty frame.
 */
export function ensureStoreCardArt(scene: CharmScene, onSettle?: () => void): Promise<void> {
  return runStoreCards(scene, 1).then(() => {
    onSettle?.();
  });
}
